import { ClientType } from "@multiweb/clients/ClientType";
import { ICultureClient } from "@multiweb/clients/culture";
import { IClientFactory } from "@multiweb/clients/IClientFactory";
import { bridge } from "@multiweb/reactCore/bridge";
import { createSetLocaleCodeAction } from "@multiweb/reactCore/i18n/actions";
import { ILanguageExtended } from "@multiweb/types/contracts";
import { CancellationToken } from "@multiweb/types/multiweb";
import { EMPTY_ITEM_RIGHTS_COLLECTION } from "Global/Base/Rights/RightTypes";
import { ApplicationStorage, AppStorageItemType } from "OW.Remoting/Remoting/ApplicationStorage";
import { Localization } from "OW.Remoting/Remoting/Localization";
import { Control } from "UI/UI/UI/Controls/Control";
import { DIContainer } from "../../../../Global/Base/DIContainer";
import { IDisposable } from "../../../../Global/Base/IDisposable";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { Caption } from "../../UI/Controls/Caption";
import { ComboBox } from "../../UI/Controls/ComboBox";
import { ControlParams, IControl } from "../../UI/Controls/ControlInterfaces";

/**
 * CustomControl pro změnu jazyka.
 */
export class LanguageSettings extends Control implements IDisposable, IControl {
    private _cultureClient: ICultureClient;
    private _languageCombo: ComboBox;

    constructor(params: ControlParams) {
        super(params);
        Caption.createCaptionsForElements(this.element); // vytvoří label elementy ke komponentám, obdobně jak to dělá AlignPanel
        this._cultureClient = DIContainer.instance.create(IClientFactory).get(ClientType.culture);
        this._buildSections();
        this._registerListeners();
        this._loadLanguages().then(a => this._fillLanguageComboBox(a));
    }

    /**
     * Načítá podporované jazyky ze serveru.
     * @returns Kolekce podporovaných jazyků.
     */
    private _loadLanguages(): Promise<readonly ILanguageExtended[]> {
        return this._cultureClient.getSupportedLanguages();
    }

    /**
     * Naplní ComboBox podporovanými jazyky.
     * @param languages Kolekce podporovaných jazyků.
     */
    private _fillLanguageComboBox(languages: readonly ILanguageExtended[]): void {
        const selectLanguage: HTMLSelectElement = this._languageCombo.element.getElementsByTagName("select")[0];
        selectLanguage.innerHTML = "";

        // nastaví jazyk na zapamatovaný ze session storage nebo na primární jazyk, pokud není uložený v session storage
        const rememberedLang = ApplicationStorage.get(AppStorageItemType.language);
        for (const lang of languages) {
            const optl = document.createElement("option");
            optl.textContent = lang.description;
            optl.setAttribute("data-culture", lang.culture);
            optl.setAttribute("data-language", lang.code);
            if ((!rememberedLang && lang.isPrimary) || (rememberedLang && rememberedLang === lang.code))
                optl.setAttribute("selected", "selected");
            selectLanguage.appendChild(optl);
        }
    }

    /**
     * Registruje obsluhu změny jazyka.
     */
    private _registerListeners(): void {
        if (this._languageCombo) {
            this._events.addListener(this._languageCombo.element, "change", async () => {
                const selectedOption = this._languageCombo.element.querySelector("option:checked");
                const langCode = selectedOption.getAttribute("data-language");
                Localization.changeLanguage(langCode, selectedOption.getAttribute("data-culture"));
                this._cultureClient.setLanguage(langCode, CancellationToken.none);
                this._cultureClient.saveLanguageProfile(langCode, CancellationToken.none);

                bridge.dispatchAction(createSetLocaleCodeAction(langCode));
            });
        }
    }

    /**
     * Vytváří html strukturu komponenty.
     */
    private _buildSections(): void {
        const settingContainer = createElement({ classes: "language-settings-container" });
        const wrp = createElement({ classes: "wrapper" });

        const languageSection = createElement({ type: "section", html: "<h3><span class=\"icon com-language\" > </span><span class=\"loc-loading\" data-lkd=\"GUI-330224\" data-lkta=\"tc\"></span > </h3>" });
        const languageComboElement = document.createElement("div");
        this._languageCombo = new ComboBox({ element: languageComboElement, originalRightsDefinition: EMPTY_ITEM_RIGHTS_COLLECTION });
        this._languageCombo.initialize();
        Localization.addLocData(languageComboElement.lastElementChild as HTMLElement, "GUI-799277", ["title", "ariaLabel"]); // Výběr jazyka
        languageSection.appendChild(languageComboElement);
        wrp.appendChild(languageSection);

        settingContainer.appendChild(wrp);

        this.element.appendChild(settingContainer);
    }

    /**
     * Funkce vrací HTML prvek reprezentující tuto tříu.
     **/
    protected _getMainContainerElement(): HTMLElement {
        return document.querySelector("LanguageSettings");
    }
}
